/**
 * Vendor-neutral access-token claim extraction.
 *
 * The staff SPA gates navigation from the Keycloak access token's claims (the claim *mapping* is
 * owned by tasks/roles-and-permissions.md); this module turns the raw JWT into a typed, SDK-free
 * [AuthClaims]. It is deliberately ADVISORY — the server re-checks every permission at action time
 * (features/roles-and-permissions.md), so a stale or absent claim only ever affects what chrome a
 * user sees, never what they are allowed to do.
 *
 * Available claims today: `tenant_id` (ADR-0006) and the realm roles in `realm_access.roles`
 * (`ACCOUNT_ROOT` for the account root, `STAFF` for activated staff). Per-venue MANAGER/WAITER roles
 * live in the DB and are enforced server-side; if/when the realm additionally emits a `venue_roles`
 * claim (a venueId→role map), this reads it so the nav lights up for non-root staff — until then
 * [AuthClaims.venueRoles] is simply empty and only the account root sees venue-scoped chrome.
 */
export type StaffRole = "WAITER" | "MANAGER" | "ROOT";

export interface AuthClaims {
  /** `tenant_id` claim — the account the token is scoped to (ADR-0006). */
  tenantId?: string;
  /** Account root (R2): implicit-everywhere. From `is_account_root` OR the `ACCOUNT_ROOT` realm role. */
  isAccountRoot: boolean;
  /** venueId → per-venue role, from the optional `venue_roles` claim. Advisory UI hint only. */
  venueRoles: Record<string, "MANAGER" | "WAITER">;
  /** Raw realm roles (`realm_access.roles`). */
  realmRoles: string[];
}

export const EMPTY_CLAIMS: AuthClaims = Object.freeze({
  isAccountRoot: false,
  venueRoles: {},
  realmRoles: [],
});

/** Decode a base64url segment to a UTF-8 string in both browser and test (jsdom) runtimes. */
function base64UrlDecode(segment: string): string {
  const normalized = segment.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), "=");
  const binary = atob(padded);
  // Reconstruct UTF-8 so multi-byte claim values (accented display names) survive.
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

function coerceVenueRoles(raw: unknown): Record<string, "MANAGER" | "WAITER"> {
  // Tolerate the two shapes a mapper might emit: an object map, or a JSON string of one.
  const map: unknown = typeof raw === "string" ? safeJsonParse(raw) : raw;
  if (!map || typeof map !== "object" || Array.isArray(map)) return {};
  const out: Record<string, "MANAGER" | "WAITER"> = {};
  for (const [venueId, role] of Object.entries(map as Record<string, unknown>)) {
    const upper = String(role).toUpperCase();
    if (upper === "MANAGER" || upper === "WAITER") out[venueId] = upper;
  }
  return out;
}

function safeJsonParse(input: string): unknown {
  try {
    return JSON.parse(input);
  } catch {
    return undefined;
  }
}

/**
 * Decode a JWT's payload segment into its raw claim object. Never throws — a missing or malformed
 * token yields `undefined`. Internal to @repo/auth (the stub path derives the Identity from it);
 * apps consume the typed [decodeJwtClaims] instead.
 */
export function decodeJwtPayload(token: string | undefined): Record<string, unknown> | undefined {
  if (!token) return undefined;
  const payloadSegment = token.split(".")[1];
  if (!payloadSegment) return undefined;
  return safeJsonParse(runCatching(() => base64UrlDecode(payloadSegment))) as
    | Record<string, unknown>
    | undefined;
}

/**
 * Parse an access-token JWT into [AuthClaims]. Never throws — a missing or malformed token yields
 * [EMPTY_CLAIMS] (fail-closed: no venue roles, not root), so the guards default to hiding chrome.
 */
export function decodeJwtClaims(token: string | undefined): AuthClaims {
  const payload = decodeJwtPayload(token);
  if (!payload) return EMPTY_CLAIMS;

  const realmAccess = payload.realm_access as { roles?: unknown } | undefined;
  const realmRoles = Array.isArray(realmAccess?.roles)
    ? (realmAccess.roles as unknown[]).map(String)
    : [];
  const isAccountRoot =
    payload.is_account_root === true ||
    payload.is_account_root === "true" ||
    realmRoles.includes("ACCOUNT_ROOT");

  return {
    tenantId: typeof payload.tenant_id === "string" ? payload.tenant_id : undefined,
    isAccountRoot,
    venueRoles: coerceVenueRoles(payload.venue_roles),
    realmRoles,
  };
}

/** Returns the decoded string, or "" if decoding threw (so JSON.parse then fails closed). */
function runCatching(fn: () => string): string {
  try {
    return fn();
  } catch {
    return "";
  }
}
